import React, { useEffect } from 'react';
import { setPageTitle } from '../../utils/title';
import Breadcrumb from '../../components/Breadcrumb';
import photoKioskImage from '../../assets/images/products/photo-kiosk-image.png';
import '../../App.css';

const breadcrumbItems = [
  { path: '/', label: 'Home' },
  { path: '/products', label: 'Products' },
  { path: '/products/photo-kiosk-backend', label: 'Photo Kiosk Backend' },
];

const references = [
  { label: 'User Guide', href: '/products/photo-kiosk-backend/user-guide' },
  { label: 'API Reference', href: '/products/photo-kiosk-backend/api-reference' },
  { label: 'Security Considerations', href: '/products/photo-kiosk-backend/security' },
  { label: 'Customer Support', href: '/products/photo-kiosk-backend/support' },
];

const customBullet = (
  <span style={{ color: '#6b46c1', fontWeight: 700, fontSize: '1.2em', marginRight: 8 }}>•</span>
);

const PhotoKioskBackend = () => {
  useEffect(() => {
    setPageTitle('Photo Kiosk Backend');
  }, []);

  return (
    <div className="products-page">
      <Breadcrumb items={breadcrumbItems} />
      <div className="section section--light" style={{ padding: 0, minHeight: '100vh' }}>
        <section
          className="page-header"
          style={{ paddingBottom: 0, paddingTop: '2.5rem', background: 'none' }}
        >
          <div
            className="container"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: '2.5rem',
              justifyContent: 'center',
              minHeight: 320,
            }}
          >
            <div style={{ flex: '1 1 350px', minWidth: 320, maxWidth: 540, marginTop: 0 }}>
              <p
                className="section__subtitle"
                style={{
                  color: '#6b46c1',
                  marginBottom: '0.75rem',
                  marginTop: 0,
                  fontSize: '1.1rem',
                }}
              >
                Go Live With Your Photo Kiosk in Minutes
              </p>
              <h1
                className="page-header__title"
                style={{ marginBottom: '0.35rem', fontSize: '2.7rem', lineHeight: 1.1 }}
              >
                Photo Kiosk Backend
              </h1>
              <p
                className="page-header__subtitle"
                style={{
                  fontSize: '1.08rem',
                  color: '#6b7280',
                  marginBottom: '1.2rem',
                  fontWeight: 400,
                }}
              >
                The only backend engine your photo kiosk might need: media upload, QR code
                generation, branded download page
              </p>
              {/* Overview Box */}
              <div
                style={{
                  border: '1.5px solid #e2e8f0',
                  background: '#fcfcfd',
                  borderRadius: 18,
                  padding: '2.2rem 2rem 2.2rem 2rem',
                  marginTop: '2rem',
                  marginBottom: 0,
                  maxWidth: 600,
                  boxSizing: 'border-box',
                  boxShadow: '0 2px 12px rgba(44,82,130,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 340,
                }}
              >
                <p
                  style={{
                    fontSize: '1.55rem',
                    color: '#4a5568',
                    fontWeight: 300,
                    lineHeight: 1.8,
                    margin: 0,
                    fontFamily: 'inherit',
                    letterSpacing: '-0.01em',
                    textAlign: 'left',
                  }}
                >
                  Photo Kiosk Backend is a turnkey backend engine for modern photo kiosks. It
                  handles media uploads, generates QR codes for instant access, and provides a
                  branded download page for your customers. Built for reliability and speed, it lets
                  you focus on the experience, not the infrastructure.
                </p>
              </div>
            </div>
            <div
              style={{
                flex: '0 0 360px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 240,
                maxWidth: 380,
              }}
            >
              <img
                src={photoKioskImage}
                alt="Photo Kiosk Backend product demo"
                style={{
                  maxWidth: 340,
                  width: '100%',
                  borderRadius: 24,
                  boxShadow: '0 8px 32px rgba(44, 82, 130, 0.12)',
                  marginBottom: 8,
                }}
              />
              <span style={{ marginTop: 8, fontSize: '0.97rem', color: '#718096' }}>
                <span
                  style={{
                    fontSize: '0.82rem',
                    color: '#A0AEC0',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    lineHeight: 1.2,
                  }}
                >
                  Example: Photo Kiosk Backend in action at a live event
                </span>
              </span>
            </div>
          </div>
        </section>
        <section style={{ paddingTop: 0 }}>
          <div className="container" style={{ maxWidth: 900, textAlign: 'left' }}>
            <h2
              className="section__title"
              style={{
                fontSize: '1.45rem',
                marginBottom: '1rem',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                textAlign: 'left',
                marginTop: '3rem',
              }}
            >
              Who Is It For?
            </h2>
            <ul
              style={{
                fontSize: '1.13rem',
                color: '#4a5568',
                marginBottom: '2.2rem',
                paddingLeft: '1.5rem',
                listStyle: 'none',
                textAlign: 'left',
              }}
            >
              <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start' }}>
                {customBullet}Photo booth and event kiosk operators who want a fast, reliable, and
                branded digital delivery experience.
              </li>
              <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start' }}>
                {customBullet}Businesses looking to modernize their photo distribution with QR codes
                and cloud storage.
              </li>
              <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start' }}>
                {customBullet}Anyone who needs a plug-and-play backend for photo/media handoff at
                events, retail, or pop-ups.
              </li>
            </ul>

            <h2
              className="section__title"
              style={{
                fontSize: '1.45rem',
                marginBottom: '1rem',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                textAlign: 'left',
              }}
            >
              How Does It Work?
            </h2>
            <ol
              style={{
                fontSize: '1.13rem',
                color: '#4a5568',
                marginBottom: '2.7rem',
                paddingLeft: '1.5rem',
                paddingTop: 12,
                paddingBottom: 12,
                paddingRight: 12,
                textAlign: 'left',
              }}
            >
              <li style={{ marginBottom: 8 }}>
                Upload photos from your kiosk or app to your dedicated backend (deployed in your AWS
                account).
              </li>
              <li style={{ marginBottom: 8 }}>
                The backend generates a unique QR code for each session or photo set.
              </li>
              <li style={{ marginBottom: 8 }}>
                Customers scan the QR code to access a branded download page, where they can view
                and download their photos.
              </li>
              <li>
                All media is securely stored and automatically expires after your chosen retention
                period.
              </li>
            </ol>

            <h2
              className="section__title"
              style={{
                fontSize: '1.45rem',
                marginBottom: '1rem',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                textAlign: 'left',
              }}
            >
              Resources & Support
            </h2>
            <div
              className="card"
              style={{
                maxWidth: 900,
                marginLeft: 0,
                marginRight: 'auto',
                padding: '2rem 2rem 1.5rem 2rem',
                marginBottom: 0,
                textAlign: 'left',
              }}
            >
              <ul
                style={{
                  fontSize: '1.13rem',
                  color: '#4a5568',
                  marginBottom: 0,
                  paddingLeft: 0,
                  listStyle: 'none',
                  textAlign: 'left',
                }}
              >
                {references.map(ref => (
                  <li key={ref.label} style={{ marginBottom: 8 }}>
                    <a href={ref.href}>{ref.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ height: '3rem', background: 'none' }} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default PhotoKioskBackend;
